<template>
  <div>
    <div id="navigator" class="flex items-center justify-start gap-3.5">
      <span id="prev-date" @click="prevDate" class="cursor-pointer">
        <ResArrowLeft class="scale-150" />
      </span>
      <div id="date-str-container" class="cursor-pointer relative" @click="showDialog = true">
        <div id="day" class="font-sans text-xs absolute -top-3 left-0">{{ dayStr }}</div>
        <p id="date-label" class="font-heading font-semibold text-xl text-fg-link-primary w-28">
          {{ dateFilterLabel }}
        </p>
      </div>
      <span id="next-date" @click="nextDate" class="cursor-pointer">
        <ResArrowRight class="scale-150" />
      </span>
    </div>

    <OnClickOutside @trigger="showDialog = false">
      <div
        v-if="showDialog"
        id="date-dialog"
        class="absolute z-20 -translate-x-1/4 xs:-translate-x-1/3 sm:translate-x-0 bg-surface-primary rounded border border-linear-subtle shadow-sm max-h-[80vh] overflow-y-auto tt-scroll"
        :class="{ 'w-[80vw]': calendarExpanded }"
      >
        <CalendarMonth
          v-show="calendarExpanded"
          :selectedDate="localDate"
          :month="monthAsNumber"
          :year="yearAsNumber"
          class="md:!mb-0"
          calendarDayClasses="md:h-32"
          @click:date="updateLocalDate"
          @click:performance="performanceSelection($event.id)"
          hideShowDetails
        >
          <template #monthTitle>
            <div
              class="flex items-center justify-center gap-4 py-4 border-b border-linear-strong sticky top-0 bg-surface-primary z-10"
            >
              <span
                id="prev-month"
                @click="prevMonth"
                class="cursor-pointer w-6 h-6 flex items-center justify-center"
              >
                <ResArrowLeft class="scale-150" />
              </span>
              <p
                id="month-label"
                class="cursor-pointer font-heading font-semibold text-xl text-fg-link-primary w-40 text-center"
              >
                {{ monthStr }} {{ yearAsNumber }}
              </p>
              <span
                id="next-month"
                @click="nextMonth"
                class="cursor-pointer w-6 h-6 flex items-center justify-center"
              >
                <ResArrowRight class="scale-150" />
              </span>
            </div>
          </template>
        </CalendarMonth>
        <DatePicker
          class="shadow-none rounded-none border-none"
          v-show="!calendarExpanded"
          :validDates="datesWithPerformances"
          :selectedDate="localDate"
          @datePickerChange="updateLocalDate($event.date)"
        />
        <div
          class="flex justify-between items-center p-4 text-fg-link-primary sticky bottom-0 bg-surface-primary border-t border-linear-subtle"
        >
          <span id="today-link" class="cursor-pointer" @click="goToToday">Today</span>
          <span
            id="expand-calendar-icon"
            class="cursor-pointer hidden lg:inline"
            @click="calendarExpanded = !calendarExpanded"
          >
            <component
              :is="calendarExpanded ? ArrowsPointingIn : ArrowsPointingOut"
              class="w-5 h-5"
            />
          </span>
        </div>
      </div>
    </OnClickOutside>
  </div>
</template>

<script setup lang="ts">
import { OnClickOutside } from '@vueuse/components'
import * as _ from 'lodash-es'
import moment from 'moment'
import { computed, onMounted, ref, toRefs, watch } from 'vue'

import ArrowsPointingIn from '@/assets/arrowsPointingIn.svg?component'
import ArrowsPointingOut from '@/assets/arrowsPointingOut.svg?component'
import ResArrowLeft from '@/assets/resArrowLeft.svg?component'
import ResArrowRight from '@/assets/resArrowRight.svg?component'
import DatePicker from '@/common/components/organisms/DatePicker.vue'
import CalendarMonth from '@/components/calendar/CalendarMonth.vue'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import { useSettingStore } from '@/stores/setting'

import { PerformanceId } from '@generated/types'

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (event: 'update:modelValue', value: string): void
  // eslint-disable-next-line no-unused-vars
  (event: 'update:performance', performanceId: PerformanceId): void
}>()

const props = withDefaults(defineProps<{ modelValue?: string }>(), {
  modelValue: undefined,
})
const { modelValue } = toRefs(props)

const showDialog = ref<boolean>(false)
const calendarExpanded = ref<boolean>(false)

const localDate = ref<string>(moment().format('YYYY-MM-DD'))
onMounted(updateLocalDateByModelValue)
watch(modelValue, updateLocalDateByModelValue)
function updateLocalDateByModelValue() {
  if (modelValue.value) updateLocalDate(modelValue.value)
}
function updateLocalDate(date: string) {
  if (date === localDate.value) return
  localDate.value = date
}
const dayStr = computed<string>(() => moment(localDate.value).format('dddd'))
function prevDate() {
  updateLocalDate(moment(localDate.value).subtract(1, 'd').format('YYYY-MM-DD'))
}
function nextDate() {
  updateLocalDate(moment(localDate.value).add(1, 'd').format('YYYY-MM-DD'))
}
function goToToday() {
  const today = moment().format('YYYY-MM-DD')
  updateLocalDate(today)
}

const monthInCalendar = ref<string>(moment(localDate.value).format('YYYY-MM-DD'))
watch(localDate, () => {
  monthInCalendar.value = moment(localDate.value).format('YYYY-MM-DD')
  if (modelValue.value !== localDate.value) emit('update:modelValue', localDate.value)
})
const monthAsNumber = computed<number>(() => moment(monthInCalendar.value).month() + 1)
const yearAsNumber = computed<number>(() => moment(monthInCalendar.value).year())
const monthStr = computed<string>(() => moment(monthInCalendar.value).format('MMMM'))
function prevMonth() {
  monthInCalendar.value = moment(monthInCalendar.value).subtract(1, 'M').format('YYYY-MM-DD')
}
function nextMonth() {
  monthInCalendar.value = moment(monthInCalendar.value).add(1, 'M').format('YYYY-MM-DD')
}

const settingStore = useSettingStore()
const dateFilterLabel = computed<string>(() =>
  settingStore.formatTemporal(localDate.value, 'MMM DD, YYYY'),
)
const performanceStore = usePerformanceStore()
const datesWithPerformances = computed<string[]>(() =>
  _.uniq(performanceStore.performances.map(p => p.date)),
)

function performanceSelection(performanceId: PerformanceId) {
  showDialog.value = false
  calendarExpanded.value = false
  emit('update:performance', performanceId)
}
</script>
